import React, { useState } from 'react';
import ShipmentBadge from './ShipmentBadge';

/**
 * ReconciliationTable
 *
 * Paginated, sortable table for the shipment operations monitor.
 *
 * Row highlighting rules (visual urgency):
 *   permanent_failed  → red tint
 *   stuck             → orange tint
 *   sla_breach        → orange tint
 *   failed_reconciling → light orange
 *   processing        → light yellow
 *   completed         → normal
 *
 * Derived fields (computed here, not from backend):
 *   processingDuration = now - processing_started_at  (minutes)
 *   isStuck            = processing AND duration > STUCK_THRESHOLD_MIN
 *   isSlaBreached      = sla_deadline < now AND not completed
 *
 * @param {Array}    orders        - filtered array of order objects
 * @param {Function} onRetry       - (order) => void — opens retry modal
 * @param {Object}   strings       - strings.adminMonitor
 */

const STUCK_THRESHOLD_MIN = 10; // minutes — configurable

function getProcessingDuration(order) {
  const started = order.shiprocket?.processing_started_at;
  if (!started) return null;
  const ms = Date.now() - new Date(started).getTime();
  return Math.floor(ms / 60000); // minutes
}

function isStuck(order) {
  if (order.shiprocket?.status !== 'processing') return false;
  const dur = getProcessingDuration(order);
  return dur !== null && dur >= STUCK_THRESHOLD_MIN;
}

function isSlaBreached(order) {
  const deadline = order.shiprocket?.sla_deadline;
  if (!deadline) return false;
  if (order.shiprocket?.status === 'completed') return false;
  return new Date(deadline) < new Date();
}

function getRowHighlight(order) {
  const status = order.shiprocket?.status;
  if (status === 'permanent_failed') return 'bg-red-50';
  if (isStuck(order) || isSlaBreached(order)) return 'bg-orange-50';
  if (status === 'failed_reconciling') return 'bg-orange-50/50';
  if (status === 'processing') return 'bg-yellow-50/50';
  return '';
}

export default function ReconciliationTable({ orders, onRetry, strings: s = {} }) {
  const [expandedId, setExpandedId] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...orders].sort((a, b) => {
    let av, bv;
    if (sortField === 'createdAt') {
      av = new Date(a.createdAt);
      bv = new Date(b.createdAt);
    } else if (sortField === 'status') {
      av = a.shiprocket?.status || '';
      bv = b.shiprocket?.status || '';
    } else if (sortField === 'processingDuration') {
      av = getProcessingDuration(a) ?? -1;
      bv = getProcessingDuration(b) ?? -1;
    } else if (sortField === 'slaDeadline') {
      av = a.shiprocket?.sla_deadline ? new Date(a.shiprocket.sla_deadline) : new Date(0);
      bv = b.shiprocket?.sla_deadline ? new Date(b.shiprocket.sla_deadline) : new Date(0);
    } else {
      return 0;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-textSecondary opacity-40">↕</span>;
    return <span className="ml-1 text-secondary">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (orders.length === 0) {
    return (
      <div className="bg-cardBg rounded-2xl shadow-card border border-secondary/10 p-12 text-center">
        <p className="text-bodyText font-sans text-lg">{s.noOrders || 'No orders match the current filters.'}</p>
      </div>
    );
  }

  return (
    <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-secondary/20">
            <tr>
              <th className="px-3 py-3 w-8" />
              <th
                className="px-3 py-3 text-left text-xs font-heading text-headingText cursor-pointer hover:text-secondary"
                onClick={() => handleSort('createdAt')}
              >
                {s.colOrderId || 'Order ID'}<SortIcon field="createdAt" />
              </th>
              <th className="px-3 py-3 text-left text-xs font-heading text-headingText">
                {s.colCustomer || 'Customer'}
              </th>
              <th className="px-3 py-3 text-left text-xs font-heading text-headingText">
                {s.colPayment || 'Payment'}
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-heading text-headingText cursor-pointer hover:text-secondary"
                onClick={() => handleSort('status')}
              >
                {s.colShipment || 'Shipment'}<SortIcon field="status" />
              </th>
              <th className="px-3 py-3 text-left text-xs font-heading text-headingText">
                {s.colAwb || 'AWB'}
              </th>
              <th className="px-3 py-3 text-left text-xs font-heading text-headingText">
                {s.colFailureReason || 'Failure Reason'}
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-heading text-headingText cursor-pointer hover:text-secondary"
                onClick={() => handleSort('processingDuration')}
              >
                {s.colDuration || 'Duration'}<SortIcon field="processingDuration" />
              </th>
              <th
                className="px-3 py-3 text-left text-xs font-heading text-headingText cursor-pointer hover:text-secondary"
                onClick={() => handleSort('slaDeadline')}
              >
                {s.colSla || 'SLA Deadline'}<SortIcon field="slaDeadline" />
              </th>
              <th className="px-3 py-3 text-left text-xs font-heading text-headingText">
                {s.colActions || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((order) => {
              const sr = order.shiprocket;
              const duration = getProcessingDuration(order);
              const stuck = isStuck(order);
              const slaBreached = isSlaBreached(order);
              const rowBg = getRowHighlight(order);

              return (
                <React.Fragment key={order._id}>
                  <tr className={`border-b border-secondary/10 transition-colors duration-150 ${rowBg} hover:brightness-95`}>
                    {/* Expand toggle */}
                    <td className="px-3 py-3">
                      <button
                        onClick={() => toggleExpand(order._id)}
                        className="text-textSecondary hover:text-secondary transition-colors"
                        title="View shipment details"
                      >
                        <svg className={`w-3.5 h-3.5 transition-transform ${expandedId === order._id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </td>

                    <td className="px-3 py-3 font-sans text-xs text-headingText font-medium whitespace-nowrap">
                      {order.orderId}
                      <div className="text-textSecondary text-xs font-normal">
                        {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </div>
                    </td>

                    <td className="px-3 py-3">
                      <div className="font-sans text-xs text-headingText">{order.customer?.name || '—'}</div>
                      <div className="font-sans text-xs text-textSecondary">{order.customer?.email || ''}</div>
                    </td>

                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-sans font-medium ${
                        order.status === 'confirmed' || order.status === 'shipped' || order.status === 'delivered'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status?.replace(/_/g, ' ').toUpperCase() || '—'}
                      </span>
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <ShipmentBadge status={sr?.status} />
                        {stuck && (
                          <span className="inline-block px-1.5 py-0.5 rounded-full text-xs font-sans font-medium bg-red-100 text-red-700">
                            STUCK
                          </span>
                        )}
                        {slaBreached && (
                          <span className="inline-block px-1.5 py-0.5 rounded-full text-xs font-sans font-medium bg-orange-100 text-orange-700">
                            SLA ⚠
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-3 py-3 font-sans text-xs text-headingText">
                      {sr?.awb_code
                        ? <span title={sr.courier_name || ''}>{sr.awb_code}</span>
                        : <span className="text-textSecondary">{s.awbPending || 'Pending'}</span>}
                    </td>

                    <td className="px-3 py-3 font-sans text-xs text-bodyText max-w-[140px]">
                      {sr?.failure_reason
                        ? <span className="text-red-700 truncate block" title={sr.failure_reason}>{sr.failure_reason}</span>
                        : <span className="text-textSecondary">—</span>}
                    </td>

                    <td className="px-3 py-3 font-sans text-xs">
                      {duration !== null
                        ? <span className={duration >= STUCK_THRESHOLD_MIN ? 'text-red-700 font-medium' : 'text-bodyText'}>
                            {duration}m
                          </span>
                        : <span className="text-textSecondary">—</span>}
                    </td>

                    <td className="px-3 py-3 font-sans text-xs">
                      {sr?.sla_deadline
                        ? <span className={slaBreached ? 'text-red-700 font-medium' : 'text-bodyText'}>
                            {new Date(sr.sla_deadline).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        : <span className="text-textSecondary">—</span>}
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExpand(order._id)}
                          className="px-2 py-1 text-xs font-sans text-secondary hover:bg-secondary/10 rounded-lg transition-colors"
                        >
                          {s.actionDetails || 'Details'}
                        </button>
                        {sr?.status === 'permanent_failed' && (
                          <button
                            onClick={() => onRetry(order)}
                            className="px-2 py-1 text-xs font-sans text-red-700 hover:bg-red-50 rounded-lg transition-colors font-medium"
                          >
                            {s.actionRetry || 'Retry'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded detail panel */}
                  {expandedId === order._id && (
                    <tr className={`border-b border-secondary/10 ${rowBg}`}>
                      <td colSpan={10} className="px-8 py-4">
                        <ShipmentDetailExpanded order={order} strings={s} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShipmentDetailExpanded({ order, strings: s = {} }) {
  const sr = order.shiprocket;
  if (!sr) {
    return <p className="text-xs font-sans text-textSecondary">{s.noShipmentData || 'No shipment data available.'}</p>;
  }

  const formatTs = (ts) =>
    ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const rows = [
    { label: 'Status', value: <ShipmentBadge status={sr.status} showTooltip={false} /> },
    { label: 'Shipment ID', value: sr.shipment_id || '—' },
    { label: 'AWB Code', value: sr.awb_code || '—' },
    { label: 'Courier', value: sr.courier_name || '—' },
    { label: 'Failure Reason', value: sr.failure_reason || '—' },
    { label: 'Processing Started', value: formatTs(sr.processing_started_at) },
    { label: 'Locked At', value: formatTs(sr.locked_at) },
    { label: 'Failed At', value: formatTs(sr.failed_at) },
    { label: 'SLA Deadline', value: formatTs(sr.sla_deadline) },
    { label: 'Est. Delivery', value: formatTs(sr.estimated_delivery_date) },
    {
      label: 'Tracking',
      value: sr.tracking_url
        ? <a href={sr.tracking_url} target="_blank" rel="noopener noreferrer" className="text-secondary underline text-xs">{s.trackLink || 'Open tracking'}</a>
        : '—',
    },
  ];

  return (
    <div>
      <p className="text-xs font-sans font-semibold text-textSecondary uppercase tracking-wide mb-3">
        {s.detailTitle || 'Full Shipment Record'}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-6 gap-y-3">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs text-textSecondary font-sans mb-0.5">{label}</p>
            <div className="text-xs font-sans text-headingText">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
